// apps/frontend/src/lesson/autogen-completion.ts
import { LessonDTO, GenerationStatus } from '@superlekcja/shared'

export type AutogenArtifact = 'slides' | 'knowledge'

export type AutogenSession = {
    active: boolean
    started: Set<AutogenArtifact>
    fired: boolean
}

const ARTIFACTS: AutogenArtifact[] = ['slides', 'knowledge']

export function newAutogenSession(active: boolean): AutogenSession {
    return { active, started: new Set(), fired: false }
}

function isTerminal(value?: GenerationStatus['value']): boolean {
    return value === 'completed' || value === 'error' || value === 'aborted'
}

/**
 * Advances the autogen session with the latest lesson state. Returns { topic }
 * exactly once — when slides finished and every artifact that started this
 * session reached a terminal state (completed/error) — otherwise null.
 * Only meaningful while session.active (armed by createLesson); manual
 * regeneration never arms a session, so it never fires.
 */
export function stepAutogenSession(session: AutogenSession, lesson: LessonDTO | null): { topic: string } | null {
    if (!session.active || session.fired || !lesson) return null

    // Record artifacts that autogen has started generating this session.
    // NOTE: firing depends on having observed each artifact in 'generating' at
    // least once. The backend streams a 'generating' status delta before
    // 'completed' (verified), so slides always land in `started`; if it ever
    // emitted 'completed' without a prior 'generating' snapshot, we'd never fire.
    for (const artifact of ARTIFACTS) {
        if (lesson[artifact]?.status.value === 'generating') {
            session.started.add(artifact)
        }
    }

    const slidesDone = session.started.has('slides') && lesson.slides?.status.value === 'completed'
    const allTerminal = [...session.started].every(a => isTerminal(lesson[a]?.status.value))

    if (slidesDone && allTerminal) {
        session.fired = true
        session.active = false
        return { topic: lesson.scenario?.prompt?.topic ?? '' }
    }
    return null
}
